import { graphHooks } from '@tunasong/graph-lib/react'
import { isDocumentEntity } from '@tunasong/models'
import type { DocumentEntity } from '@tunasong/models'
import type { Entity, Media, Persisted } from '@tunasong/schemas'

/**
 * Pick the linked song/page document for a media entity. Precedence (fail-soft):
 *
 *  1. The first `hasRecording` backlink source that is a document entity
 *     (song or page) — the explicit "this audio is a recording of that song"
 *     link created by playlist auto-linking or the audio layout's connect flow.
 *  2. The media's parent entity, when it is a document entity (audio uploaded
 *     directly under a song).
 *  3. `null` — no linked song. A bare folder parent does NOT count.
 *
 * Single definition shared by the playlist row indicator and the performance
 * stage so they never disagree. Pure function — the graph queries live in
 * `useLinkedSong`; this keeps the precedence unit-testable without a graph cache.
 */
export const pickLinkedSong = (
  backlinkSources: readonly Persisted<Entity>[],
  parentEntity: Persisted<Entity> | undefined
): Persisted<DocumentEntity> | null => {
  const linked = backlinkSources.find(isDocumentEntity)
  if (linked) {
    return linked as Persisted<DocumentEntity>
  }
  if (parentEntity && isDocumentEntity(parentEntity)) {
    return parentEntity as Persisted<DocumentEntity>
  }
  return null
}

/**
 * Resolve the linked song for a media entry. Always mounted — pass `undefined`
 * while no entry is active; hooks run with empty inputs.
 *
 * The `hasRecording` link is stored `song --hasRecording--> audio` (source =
 * song, target = audio; see `frontend/search/src/autolink.hook.ts` with
 * `direction: 'in'`). The song is therefore an INCOMING edge on the audio,
 * fetched via `useEntityBacklinks` — NOT `useEdgeEntities`, which is keyed on
 * `sourceId` and would `skipToken` on a target-only query (the v1 bug that
 * left the performance stage empty). Backlink edges carry ids only, so the
 * source ids are resolved through `useEntitiesById`.
 */
export const useLinkedSong = (
  media?: Persisted<Media>
): { song: Persisted<DocumentEntity> | null; isLoading: boolean } => {
  const { backlinks, isLoading: backlinksLoading } = graphHooks.useEntityBacklinks({
    entityId: media?.id,
    relation: 'hasRecording',
  })
  const sourceIds = backlinks.map(edge => edge.source)
  const { entities: backlinkSources, isLoading: sourcesLoading } = graphHooks.useEntitiesById(sourceIds)
  const { entity: parent, isLoading: parentLoading } = graphHooks.useEntity(media?.parentId)
  if (!media) {
    return { song: null, isLoading: false }
  }
  return {
    song: pickLinkedSong(backlinkSources, parent),
    isLoading: Boolean(backlinksLoading || sourcesLoading || parentLoading),
  }
}
